import { useState } from 'react'
import { toast } from 'sonner'
import { Trash2 } from 'lucide-react'
import { Button, Chip, Drawer, Input } from '@/components/ui'
import { useCreateStage, useDeleteStage, usePatchStage, useStages } from '../api/queries'

export interface StageManagerDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Stage manager — a Drawer (not a Modal) per the "modals last resort" rule.
 * Lists support stages with inline rename, color, terminal toggle, add, and
 * delete-with-migration.
 */
export function StageManagerDrawer({ open, onOpenChange }: StageManagerDrawerProps) {
  const stages = useStages()
  const patch = usePatchStage()
  const create = useCreateStage()
  const del = useDeleteStage()
  const [newName, setNewName] = useState('')

  const items = [...(stages.data?.items ?? [])].sort((a, b) => a.sequence - b.sequence)

  return (
    <Drawer open={open} onOpenChange={onOpenChange} size="standard" title="Manage stages">
      <div className="flex flex-col gap-2">
        {items.map((s) => (
          <div
            key={s.id}
            className="flex items-center gap-2 rounded-sm border border-border-subtle bg-surface p-2"
          >
            <Input
              defaultValue={s.name}
              className="flex-1"
              onBlur={(e) => {
                if (e.target.value && e.target.value !== s.name) {
                  patch.mutate({ id: s.id, edits: { name: e.target.value } })
                }
              }}
            />
            <input
              type="color"
              defaultValue={s.color ?? undefined}
              title="Stage color"
              className="h-8 w-8 cursor-pointer rounded-sm border border-border-subtle"
              onBlur={(e) => {
                if (e.target.value !== s.color) {
                  patch.mutate({ id: s.id, edits: { color: e.target.value } })
                }
              }}
            />
            <label className="flex items-center gap-1 text-role-metadata text-tertiary">
              <input
                type="checkbox"
                defaultChecked={s.is_terminal}
                onChange={(e) =>
                  patch.mutate({ id: s.id, edits: { is_terminal: e.target.checked } })
                }
              />
              terminal
            </label>
            {s.is_default ? <Chip size="xs" variant="info">default</Chip> : null}
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Delete ${s.name}`}
              onClick={() => {
                const targets = items.filter((x) => x.id !== s.id)
                const migrateTo = targets[0]?.id
                if (
                  !window.confirm(
                    `Delete "${s.name}"? Tickets on it will move to "${
                      targets[0]?.name ?? '(none)'
                    }".`,
                  )
                )
                  return
                del.mutate(
                  { id: s.id, migrateToStageId: migrateTo },
                  {
                    onSuccess: () => toast.success('Stage deleted'),
                    onError: (err) => toast.error(String(err)),
                  },
                )
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <div className="mt-2 flex items-center gap-2 border-t border-border-subtle pt-3">
          <Input
            placeholder="New stage name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="flex-1"
          />
          <Button
            size="sm"
            variant="secondary"
            loading={create.isPending}
            disabled={!newName.trim()}
            onClick={() =>
              create.mutate(
                { name: newName.trim() },
                {
                  onSuccess: () => {
                    setNewName('')
                    toast.success('Stage added')
                  },
                  onError: (e) => toast.error(String(e)),
                },
              )
            }
          >
            Add stage
          </Button>
        </div>
      </div>
    </Drawer>
  )
}
